import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  CircularProgress
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowBackIos, ArrowForwardIos, Event } from "@mui/icons-material";
import { format } from "date-fns";
import { ru, ro } from "date-fns/locale";
import { usePreventVerticalScroll } from "./PaymentWidget";
import { DashboardWidgetsClient } from "../../../../api/DashboardWidgetsClient";
import { REACT_APP_PORTAL_API_URL } from "../../../../KeyVariables";
import { AppointmentDashboardDTO } from "../../../../api/DashboardWidgetDTO";
import { PatientWidgetProps } from "../PatientDashboard";
import { useNavigate } from "react-router-dom";

export const AppointmentsWidget = ({ patientId }: PatientWidgetProps) => {
  const [items, setItems] = useState<AppointmentDashboardDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation("Portal");
  const refApp = useRef(null);
  const navigate = useNavigate();

  const getLocale = () => {
    switch (i18n.language) {
      case "ru":
        return ru;
      case "ro":
        return ro;
      default:
        return ro;
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await new DashboardWidgetsClient(REACT_APP_PORTAL_API_URL).appointments(patientId > 0 ? patientId : undefined);
      setItems(data.sort((a, b) => b.date - a.date));
    } catch (error) {
      console.error("Error fetching appointment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const [weekDays, setWeekDays] = useState<Date[]>([]);
  useEffect(() => {
    if (refApp.current) {
      refApp.current.scrollLeft = refApp.current.scrollWidth;
    }
  }, [weekDays]); 

  useEffect(() => {
    const uniqueDates = Array.from(
      new Set(items.map(item => format(new Date(item.date!), "yyyy-MM-dd")))
    ).map(dateStr => new Date(dateStr));

    uniqueDates.sort((a, b) => a.getTime() - b.getTime());
    setWeekDays(uniqueDates);
  }, [items]);

  const handleScroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction * 300, behavior: "smooth" });
    }
  };

  usePreventVerticalScroll(refApp, 300);


  return (
    <Box >
      <Grid container alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
        <IconButton onClick={() => handleScroll(refApp, -1)}>
          <ArrowBackIos fontSize="small" />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Event fontSize="medium" />
          <Typography variant="h5">{t('Portal_Appointments')}</Typography>
          {isLoading && <CircularProgress size={20}/>}
        </Box>
        <IconButton onClick={() => handleScroll(refApp, 1)}>
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Grid>

      <TableContainer
        component={Paper}
        ref={refApp}
        sx={{
          display: "flex",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          gap: 1,
          flexGrow: 1,
          scrollBehavior: "smooth",
        }}
      >
        {
          weekDays?.length > 0 ?
          <Table>
          <TableHead>
            <TableRow>
              {weekDays.map((day, index) => (
                <TableCell key={index} align="center" sx={{ py: 0.5 }}>
                  <Typography variant="h6">{format(day, "dd.MM.yyyy")}</Typography>
                  <Typography variant="body2">
                    {format(day, "EEEE", { locale: getLocale() })}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {weekDays.map((day, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{ border: "1px solid #ccc", verticalAlign: "top" }}
                >
                  {items
                    .filter(
                      (item) =>
                        format(item.date!, "yyyy-MM-dd") ===
                        format(day, "yyyy-MM-dd")
                    )
                    .map((item, idx) => {
                      const isPast = new Date(item.date!) < new Date();
                      return (
                        (
                          <Box
                            key={idx}
                            sx={{
                              backgroundColor: isPast ? "rgba(255, 240, 107, 0.1)" : "rgba(0, 255, 0, 0.1)",
                              p: 1,
                              mt: 1,
                              width: "20vh",
                              borderRadius: 2,
                              textAlign: "center",
                              minWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            <Typography variant="h6">
                              {format(item.date!, "HH:mm")}
                            </Typography>
                            <Typography variant="subtitle1">
                              {item.service?.name}
                            </Typography>
                            <Typography variant="caption">
                              {item.doctor?.name}
                            </Typography>
                            <Typography variant="body1">
                              {item.clinic?.name}
                            </Typography>
                          </Box>
                        )
                      )
                    }
                    )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
        : 
        <Typography sx={{p:1}}>{t('Portal_NoAppointments')}</Typography>
      } 
      </TableContainer>
      <Box display="flex" justifyContent="flex-end" mt={0.5} mr={1}>
        <Button variant="contained" size="small" onClick={() => window.open('/patient-archive?Tab=appointments' + (patientId > 0 ? `&PatientId=${patientId}` : ''), '_self')}>
          {t('Portal_ArchiveAppointments')}
        </Button>
      </Box>
    </Box>
  );
};
